import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { sessionsApi } from '../../api/sessions';
import ChatInterface from './ChatInterface';
import {
  CHAT_FOCUS_EVENT,
  CHAT_FOCUS_SECTION_EVENT,
  CHAT_SCENE_REMOVED_EVENT,
  CHAT_SUBJECT_EVENT,
} from './ChatPaneFrame';
import { CHAT_ATTACH_EVENT, publishChatAttachment } from './chatAttachment';
import {
  ROOM_CHAT_OPENING,
  readRoomSessionId,
  rememberRoomSessionId,
  roomKeyForSubject,
} from '../../utils/roomChat';
import './ChatPaneFrame.css';

/**
 * Right-hand chat for Outline, Write, Notes, and Docs.
 * Idea is the chat page itself and does not get a second pane.
 * Each room keeps its own thread. If the room column is missing,
 * the pane keeps the one shared chat.
 */
export function paneSessionId(subject, currentSessionId, discoverySessionId) {
  if (
    subject === 'Write'
    && discoverySessionId
    && String(currentSessionId || '') === String(discoverySessionId)
  ) {
    return null;
  }
  return currentSessionId;
}

export default function RoomChatPane({ subject }) {
  const { currentSessionId, currentStory, currentProject } = useApp();
  const roomKey = roomKeyForSubject(subject);
  const [focus, setFocus] = useState('');
  const [focusSectionId, setFocusSectionId] = useState('');
  // One pane serves every room. Write publishes a scene title and id
  // onto it; leaving that room must drop both, or Outline's header and
  // the next send keep Write's scene. A click in the room the writer
  // is in can set them again. Resetting here leaves the chat mounted.
  const focusScope = `${subject || ''}\0${roomKey || ''}`;
  const [focusScopeSeen, setFocusScopeSeen] = useState(focusScope);
  if (focusScope !== focusScopeSeen) {
    setFocusScopeSeen(focusScope);
    setFocus('');
    setFocusSectionId('');
  }
  const focusSectionRef = useRef('');
  focusSectionRef.current = focusSectionId;
  const [attachment, setAttachment] = useState(null);
  const [gate, setGate] = useState('pending');
  const [roomSessionId, setRoomSessionId] = useState(null);
  const gatedKeyRef = useRef(null);
  const heldStoryIdRef = useRef(null);
  const storyId = currentStory?.id || null;
  if (storyId) heldStoryIdRef.current = storyId;
  const writeSceneId = roomKey === 'write' ? (focusSectionId || '') : '';

  useEffect(() => {
    const onFocus = (event) => setFocus(String(event.detail || ''));
    const onSection = (event) => setFocusSectionId(String(event.detail || ''));
    window.addEventListener(CHAT_FOCUS_EVENT, onFocus);
    window.addEventListener(CHAT_SUBJECT_EVENT, onFocus);
    window.addEventListener(CHAT_FOCUS_SECTION_EVENT, onSection);
    const onRemoved = (event) => {
      const id = String(event.detail || '');
      if (!id || String(focusSectionRef.current) !== id) return;
      setFocus('');
      setFocusSectionId('');
    };
    window.addEventListener(CHAT_SCENE_REMOVED_EVENT, onRemoved);
    return () => {
      window.removeEventListener(CHAT_FOCUS_EVENT, onFocus);
      window.removeEventListener(CHAT_SUBJECT_EVENT, onFocus);
      window.removeEventListener(CHAT_FOCUS_SECTION_EVENT, onSection);
      window.removeEventListener(CHAT_SCENE_REMOVED_EVENT, onRemoved);
    };
  }, []);
  useEffect(() => {
    const onAttach = (event) => setAttachment(event.detail || null);
    window.addEventListener(CHAT_ATTACH_EVENT, onAttach);
    return () => window.removeEventListener(CHAT_ATTACH_EVENT, onAttach);
  }, []);

  useEffect(() => {
    if (!storyId || !roomKey) return undefined;
    const key = `${storyId}:${roomKey}:${writeSceneId}`;
    // The same book can drop out and come back while a reply is on screen.
    // Opening the room again would throw that thread away.
    // Write includes the scene, so a scene change opens that scene's thread.
    if (gatedKeyRef.current === key) return undefined;
    let cancelled = false;
    setGate('pending');
    const prefer = readRoomSessionId(storyId, roomKey, writeSceneId || null);
    sessionsApi.roomChat(storyId, roomKey, prefer, writeSceneId || null).then((data) => {
      if (cancelled) return;
      gatedKeyRef.current = key;
      const body = data?.data || data;
      if (!body || body.shared !== false) {
        setGate('shared');
        return;
      }
      const id = body.session_id || null;
      setRoomSessionId(id);
      rememberRoomSessionId(storyId, roomKey, id, writeSceneId || null);
      setGate('room');
    }).catch(() => {
      if (cancelled) return;
      gatedKeyRef.current = key;
      setGate('shared');
    });
    return () => { cancelled = true; };
  }, [storyId, roomKey, writeSceneId]);

  const shownStoryId = storyId || heldStoryIdRef.current;
  if (!shownStoryId) return null;
  const title = roomPaneSubject(subject, focus);
  const shared = gate !== 'room';
  const pin = (id) => {
    setRoomSessionId(id || null);
    rememberRoomSessionId(shownStoryId, roomKey, id || null, writeSceneId || null);
  };

  return (
    <aside className="room-chat-pane" aria-label="Chat">
      {gate === 'pending' ? (
        <p className="chat-pane__menu-empty">{ROOM_CHAT_OPENING}</p>
      ) : (
        <ChatInterface
          sessionId={shared
            ? paneSessionId(subject, currentSessionId, currentProject?.discovery_session_id)
            : roomSessionId}
          pane
          subject={title}
          preferSubject={title !== subject}
          roomKey={shared ? null : roomKey}
          pinSession={!shared}
          onPinnedSession={shared ? null : pin}
          attachment={attachment}
          focusSectionId={focusSectionId || null}
          onClearAttachment={() => publishChatAttachment(null)}
        />
      )}
    </aside>
  );
}

/**
 * Notes and Docs name the room. A scene title from Outline or Write
 * must not stay on those panes. Outline and Write keep a scene title.
 */
export function roomPaneSubject(roomSubject, focus) {
  if (roomSubject === 'Notes' || roomSubject === 'Docs') return roomSubject;
  const pinned = String(focus || '').trim();
  return pinned || roomSubject;
}

export function chatPaneSubject(pathname) {
  const path = pathname || '';
  if (path.startsWith('/chat') || path.startsWith('/sessions')) return null;
  if (path.startsWith('/plottrack')) return 'Outline';
  if (path.startsWith('/scene-writing')) return 'Write';
  if (path.startsWith('/bibles')) return 'Notes';
  if (path.includes('/notes')) return 'Docs';
  return null;
}
